export type WindowPoint = { x: number; y: number };
export type WindowSize = { width: number; height: number };
export type OccupiedWindow = {
  position: WindowPoint;
  size: WindowSize;
  isMinimized?: boolean;
  /**
   * Cost multiplier for covering this window in the least-overlap search.
   * Defaults to 1. A large weight makes the search cover any amount of
   * ordinary content before it covers this window.
   */
  overlapWeight?: number;
  /**
   * App chrome rather than a window, such as the desktop drag rail. The
   * search covers any amount of window content before it covers chrome,
   * and covers as little chrome as it can when every position does.
   */
  keepOut?: boolean;
};

export const clampWindowPosition = (
  position: WindowPoint,
  size: WindowSize,
  viewport: WindowSize,
  allowEdgeOverflow = false
): WindowPoint => {
  if (allowEdgeOverflow) {
    return {
      x: Math.max(-size.width / 2, Math.min(position.x, viewport.width - size.width / 2)),
      y: Math.max(-size.height / 2, Math.min(position.y, viewport.height - size.height / 2)),
    };
  }

  return {
    x: Math.max(0, Math.min(position.x, viewport.width - size.width)),
    y: Math.max(0, Math.min(position.y, viewport.height - size.height)),
  };
};

export const windowPointsMatch = (a: WindowPoint, b: WindowPoint) =>
  a.x === b.x && a.y === b.y;

const overlapArea = (
  first: { x: number; y: number; width: number; height: number },
  second: { x: number; y: number; width: number; height: number }
) => {
  const width = Math.max(
    0,
    Math.min(first.x + first.width, second.x + second.width) - Math.max(first.x, second.x)
  );
  const height = Math.max(
    0,
    Math.min(first.y + first.height, second.y + second.height) - Math.max(first.y, second.y)
  );
  return width * height;
};

/**
 * When a workspace is completely saturated, choose the position that obscures
 * the least existing content. This is deliberately a degraded fallback: the
 * normal layout planner still owns every collision-free placement.
 *
 * Candidates are the preferred point, the corners, every position flush
 * against an occupied rect (each such x with each such y), and a `step`
 * lattice. The flush positions let the window sit right beside a window or
 * right below the drag rail instead of only on lattice points. Scores
 * compare covered chrome first, then weighted covered window area, then the
 * number of windows covered, then the distance to the preferred point.
 */
export const findLeastOverlappingWindowPosition = (
  size: WindowSize,
  viewport: WindowSize,
  occupiedWindows: OccupiedWindow[],
  preferredPosition?: WindowPoint,
  margin = 3,
  step = 24
): WindowPoint => {
  const maxX = Math.max(margin, viewport.width - size.width - margin);
  const maxY = Math.max(margin, viewport.height - size.height - margin);
  const preferred = clampWindowPosition(
    preferredPosition ?? {
      x: (viewport.width - size.width) / 2,
      y: (viewport.height - size.height) / 2,
    },
    size,
    viewport
  );
  const candidateKeys = new Set<string>();
  const candidates: WindowPoint[] = [];
  const addCandidate = (candidate: WindowPoint) => {
    const clamped = {
      x: Math.max(margin, Math.min(maxX, Math.floor(candidate.x))),
      y: Math.max(margin, Math.min(maxY, Math.floor(candidate.y))),
    };
    const key = `${clamped.x},${clamped.y}`;
    if (candidateKeys.has(key)) return;
    candidateKeys.add(key);
    candidates.push(clamped);
  };

  const occupiedRects = occupiedWindows
    .filter((window) => !window.isMinimized)
    .map((window) => {
      // Windows keep a margin on both sides; chrome has none of its own.
      const outset = window.keepOut ? 0 : margin;
      return {
        x: window.position.x - outset,
        y: window.position.y - outset,
        width: window.size.width + outset * 2,
        height: window.size.height + outset * 2,
        weight: window.overlapWeight ?? 1,
        keepOut: Boolean(window.keepOut),
      };
    });

  addCandidate(preferred);
  addCandidate({ x: margin, y: margin });
  addCandidate({ x: maxX, y: margin });
  addCandidate({ x: margin, y: maxY });
  addCandidate({ x: maxX, y: maxY });

  // A candidate's occupancy rect is (x - margin, size + 2 * margin), so it is
  // flush against an occupied rect at these positions.
  const flushXs = new Set<number>();
  const flushYs = new Set<number>();
  for (const rect of occupiedRects) {
    flushXs.add(rect.x + rect.width + margin);
    flushXs.add(rect.x - size.width - margin);
    flushYs.add(rect.y + rect.height + margin);
    flushYs.add(rect.y - size.height - margin);
  }
  for (const y of flushYs) {
    for (const x of flushXs) addCandidate({ x, y });
    addCandidate({ x: preferred.x, y });
  }
  for (const x of flushXs) addCandidate({ x, y: preferred.y });

  for (let y = margin; y <= maxY; y += Math.max(1, step)) {
    for (let x = margin; x <= maxX; x += Math.max(1, step)) {
      addCandidate({ x, y });
    }
  }
  addCandidate({ x: maxX, y: maxY });

  return candidates.reduce((best, candidate) => {
    const candidateRect = {
      x: candidate.x - margin,
      y: candidate.y - margin,
      width: size.width + margin * 2,
      height: size.height + margin * 2,
    };
    const overlaps = occupiedRects.map((rect) => ({
      area: overlapArea(candidateRect, rect),
      weight: rect.weight,
      keepOut: rect.keepOut,
    }));
    const windowOverlaps = overlaps.filter((overlap) => !overlap.keepOut);
    const score = {
      chrome: overlaps.reduce((sum, overlap) => sum + (overlap.keepOut ? overlap.area : 0), 0),
      area: windowOverlaps.reduce((sum, overlap) => sum + overlap.area * overlap.weight, 0),
      count: windowOverlaps.filter((overlap) => overlap.area > 0).length,
      distance: Math.hypot(candidate.x - preferred.x, candidate.y - preferred.y),
    };
    if (!best) return { candidate, score };
    if (score.chrome !== best.score.chrome) return score.chrome < best.score.chrome ? { candidate, score } : best;
    if (score.area !== best.score.area) return score.area < best.score.area ? { candidate, score } : best;
    if (score.count !== best.score.count) return score.count < best.score.count ? { candidate, score } : best;
    if (score.distance !== best.score.distance) {
      return score.distance < best.score.distance ? { candidate, score } : best;
    }
    if (candidate.y !== best.candidate.y) return candidate.y < best.candidate.y ? { candidate, score } : best;
    return candidate.x < best.candidate.x ? { candidate, score } : best;
  }, null as null | { candidate: WindowPoint; score: { chrome: number; area: number; count: number; distance: number } })!
    .candidate;
};
